import { createResourceStore } from "@/lib/resource";
import type { Product } from "./Product";
import type { Shop } from "./Shop";
import type { Supplier } from "@/store/Supplier";
import type { Warehouse } from "./Warehouse";
import type { StockQuantity } from "./StockQuantity";
import type { FinanceTransaction } from "./FinanceTransaction";
import type { FinanceDebt } from "./FinanceDebt";

export interface StockBatch {
  __type: "StockBatch";
  id: string;
  supplier_id?: string;
  warehouse_id?: string;
  shop_id?: string;
  product_id: string;
  unit_price: number;
  selling_price: number; // deriv from product.selling_price
  quantity: number;
  quantity_adjusted: number;
  quantity_transfer_in: number;
  quantity_transfer_out: number;
  quantity_sold: number;
  quantity_sale_return: number;
  quantity_supply_return: number;
  batch_code?: string;
  expiry_date?: string;
  manufacturing_date: string;
  description: string;
  created_at?: string;
  updated_at?: string;
  warehouse: Warehouse;
  supplier: Supplier;
  shop: Shop;
  product: Product;
  quantities: StockQuantity[];
  transactions: FinanceTransaction[];
  debts: FinanceDebt[];
}

export const useStockBatch = createResourceStore<StockBatch>(
  "stock-batches",
  () => ({
    stats: {
      filters: {
        start_date: null,
        end_date: null,
      },
      total_batches: 0,
      total_supply_cost: 0.0,
      cash_out_flow: 0.0,
      net_debt_obligation: 0.0,
      total_qty_added: 0,
      total_qty_removed: 0,
      net_adjustment_qty: 0,
      series: {
        labels: [],
        datasets: [],
      },
      top_supplied_products: [],
      top_suppliers: [],
    },
    transform: (item: StockBatch) => {
      return {
        ...item,
        __type: "StockBatch",
      };
    },
  })
);

/**
 * Calcule la marge brute sans double déduction des retours fournisseurs
 * car ils sont déjà inclus dans le coût net du stockBatch.
 */
export const computeGrossMargin = (
  saleStats: any,
  saleReturnStats: any,
  stockBatchStats: any
) => {
  // 1. Chiffre d'Affaires Net (Revenus réels)
  // On déduit les retours clients car SaleStats.total_sales_volume donne le brut
  const netSales =
    saleStats.total_sales_volume - saleReturnStats.total_returned_amount;

  // 2. Coût de la Marchandise (Déjà net selon ta logique contrôleur)
  // total_supply_cost = Cash + (Dettes Fournisseurs - Retours Fournisseurs)
  const netSupplyCost = stockBatchStats.total_supply_cost;

  // 3. Marge Brute
  const grossMargin = netSales - netSupplyCost;

  // 4. Calcul du pourcentage pour l'analyse de performance
  const marginPercentage = netSales > 0 ? (grossMargin / netSales) * 100 : 0;

  return {
    netSales, // Ce qui est vraiment entré
    netSupplyCost, // Ce qui est vraiment sorti (ajusté des avoirs fournisseurs)
    grossMargin, // Le profit réel
    marginPercentage: marginPercentage.toFixed(2) + "%",
    isProfitable: grossMargin > 0,
  };
};
